/**
 * `WorkflowScriptRunner.ts` — web port of Unity's
 * `Models/WorkflowModel/WorkflowScriptRunner.cs`.
 *
 * Plays a workflow's self-contained scripts straight from the workflow document. Unity
 * materializes each script into a transient `ActionData` and hands it to the scene replay engine;
 * the web port has no replay engine, so this runner executes the steps directly:
 *   - `say` steps route the verbatim line into the realtime session as an "INTERNAL SAY:" message
 *     (the base prompt's "# Internal Speech Messages" section makes Spark speak it verbatim), then
 *     await the response finishing. The line is flattened out of Markdown first
 *     (`markdownSpeech.ts`) — "verbatim" is what makes a `**bold**` marker something to pronounce
 *     rather than something to interpret;
 *   - tool steps await `AgentToolExecutorModel.executeAsync(toolId, argsJson)` and any
 *     tool-specific playback effect (currently finite highlight pulses);
 *   - a following `do_not_wait` marker lets the preceding step overlap later work, matching Unity.
 *
 * Revert (`revert: on_action_finish` / `on_scene_change`) is snapshot-and-restore, ported in
 * `WorkflowRevertState.ts`: the pre-tool state is captured before every tool step and put back
 * either when the script ends or when the scene it ran in is left. Unity gets this from the replay
 * engine's `PreviewRestoreState`; the runner owns it here because the web has no replay engine.
 *
 * Spark's OWN changes during a run — a learner asking it to hide, isolate or highlight something —
 * are captured the same way (`beginLiveToolCall`, fed by the executor's start event) and undone on
 * every step move, before the outgoing script's own revert (`revertLiveChanges`, AI-1424) — and,
 * when the move changes scene, not until that scene has been left, like an `on_scene_change`
 * revert (AI-1429), so the old scene never shows them coming off. The step's script outranks Spark
 * on anything both touch. Web-only: Unity's Guide has no equivalent.
 *
 * A REPLAY of a step puts the step back as the learner arrived to find it before playing it again
 * — a per-step record of the same captures (`m_stepChanges`), clips restored to where they were
 * rather than restarted — and then plays the step's takes from the start (AI-1459). Every other
 * arrival leaves a step's changes to its declared revert mode, as before. Web-only too: Unity's
 * replay re-runs the script over its own end state.
 *
 * One exception to "as before", for animation only: when the run LEAVES a scene, its models get back
 * the animation they had when the run arrived there (`m_sceneEntryAnimation`), whatever the revert
 * mode. The renderer restarts an autoplaying take whenever its scene comes back on screen, so a take
 * one step started played again on its own when the learner went back to that step, before the
 * step's script played it once more.
 *
 * Web deviations (deliberate):
 *   - Project mutations are a runtime-only overlay for the whole playback.
 *     Declared revert modes still decide when an effect ends during the run;
 *     `revert: none` means "keep it until playback ends." No mode persists
 *     project data.
 *   - Structural commands are refused because no complete transient overlay
 *     exists for scene/model/list topology.
 *   - No pause/resume yet (Unity rides the replay engine's pause) — stop only.
 */

import { Emitter } from "@core/events/emitter";
import { AgentToolNames } from "@ai/registry/agentToolNames";
import {
  isScriptContextTool,
  isStepScopedTool,
  isStructuralScriptCommand,
} from "@ai/registry/scriptCommandCatalog";
import { beginWorkflowPerformanceOperation } from "@models/project/SceneNavigationDiagnosticLogger";
import type {
  WorkflowDocument,
  WorkflowScript,
  WorkflowScriptRevert,
  WorkflowStep,
} from "./WorkflowDocument";
import { markdownToSpeechText } from "@models/utils/markdownSpeech";
import { resolveSayText } from "./WorkflowDocument";
import { resolveWorkflowText } from "./workflowTextTokens";
import type { WorkflowMediaControlResult } from "./IWorkflowRunModel";
import {
  captureToolRevertState,
  resolveAnimationTargetEntityIds,
  WorkflowRevertLedger,
  type WorkflowRevertDependencies,
  type WorkflowRevertViewOptions,
} from "./WorkflowRevertState";

const K_ACTION_ID_PREFIX = "wf:";
/** Upper bound on one say-step's speech (safety net when response events misbehave). */
const K_SAY_TIMEOUT_MS = 60_000;

/** `wait_for` delay used when none is authored, or an unusable one is. */
const K_DEFAULT_WAIT_SECONDS = 3;
/** Ceiling on an authored `delaySeconds` — five minutes is already a typo. */
const K_MAX_WAIT_SECONDS = 300;
/**
 * Ceiling on a `wait_for` user_response. A learner who walks away must not
 * hold the script — and its pending revert — open for the life of the tab.
 */
const K_USER_RESPONSE_TIMEOUT_MS = 10 * 60_000;
/** How often a parked wait re-checks its run for cancellation. */
const K_CANCEL_POLL_MS = 100;
/** How long `play_video` waits for the step's media surface to mount. */
const K_VIDEO_READY_TIMEOUT_MS = 10_000;
/**
 * Ceiling on how long a step move's undo of Spark's changes waits for one of
 * Spark's tool calls that is still running. The wait exists so the call cannot
 * land after the undo; the ceiling so a call that never settles cannot hold
 * every later restore — and the next step's first capture — behind it.
 */
const K_LIVE_TOOL_SETTLE_TIMEOUT_MS = 10_000;

/** A cancellable sleep. Resolves early (rather than rejecting) when aborted. */
function delayAsync(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise<void>((resolve) => {
    if (signal.aborted) {
      resolve();
      return;
    }
    const timer = window.setTimeout(() => {
      signal.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    function onAbort(): void {
      window.clearTimeout(timer);
      resolve();
    }
    signal.addEventListener("abort", onAbort, { once: true });
  });
}

const K_VALID_TOOL_IDS = new Set(Object.values(AgentToolNames).map((id) => id.toLowerCase()));

/** lowercase wire id → canonical wire id (Unity `FromId` → `GetId` round trip). */
const K_CANONICAL_TOOL_IDS = new Map(
  Object.values(AgentToolNames).map((id) => [id.toLowerCase(), id] as const),
);

/** What the runner needs from the AI harness (provided by the orchestrator). */
export interface WorkflowScriptHost {
  /** Executes one tool step; resolves with the "ok:"/"error:" tool result. */
  executeToolAsync(toolId: string, args: Record<string, unknown>): Promise<string>;
  /** Speaks a verbatim line through the session ("INTERNAL SAY:" message). */
  sayVerbatim(text: string): void;
  /**
   * Resolves TRUE once the session can actually carry a say, FALSE on the
   * host's own timeout or when `signal` aborts.
   *
   * ⚠ A say sent before the session is ready is dropped SILENTLY — no error,
   * no retry, nobody behind it to notice. Three windows on a reload: no
   * session object yet (the orchestrator awaits three prompt-catalog fetches
   * before it even connects), the socket not yet open, and — the one that
   * looks connected — the ~450 ms between the socket opening and the server's
   * `session.created`, where the proxy drops the item AND the instructions
   * carrying the `INTERNAL SAY:` contract have not been flushed.
   *
   * A viewer-role learner meets all three: their autostart fires
   * SYNCHRONOUSLY on `onLoadProjectCompleted` with no settle dwell, so the run
   * routinely starts before the session is up and step one narrates nothing.
   *
   * Optional: a headless host has no session, and one that does not implement
   * it keeps the old fire-and-forget behaviour.
   */
  waitForSpeechReadyAsync?(signal: AbortSignal): Promise<boolean>;
  /** Resolves when the active response (the spoken say line) finishes, or on timeout. */
  waitForResponseIdleAsync(timeoutMs: number): Promise<void>;
  /** Resolves when playback-specific effects started by a successful tool have finished. */
  waitForToolEffectsIdleAsync?(
    toolId: string,
    args: Record<string, unknown>,
  ): Promise<void>;
  /**
   * Starts the owning step's video through the mounted media surface.
   * `unavailable` is transient while its URL resolves and element mounts.
   */
  playCurrentStepVideo?(stepName: string): WorkflowMediaControlResult;
  /**
   * How many turns the learner has taken this session — spoken or typed.
   *
   * Read alongside {@link waitForUserTurnAsync} so a wait can tell whether the
   * learner ALREADY answered. A script asks its question in a `say` step, and
   * that step is awaited on its own before the loop reaches the wait — so a
   * learner who answers over the question does it while nothing is listening.
   * Counting turns continuously is what closes that window; a listener attached
   * at the wait, however early, cannot.
   */
  getUserTurnCount?(): number;
  /**
   * Resolves on the learner's next turn — spoken or typed — or on timeout, or
   * when `signal` aborts. Backs a `wait_for` step with `trigger: user_response`.
   *
   * Resolves immediately when {@link getUserTurnCount} has already moved past
   * `sinceTurnCount`: that is the learner having answered during the step
   * before this one.
   *
   * Resolves TRUE only when a real turn arrived, so the caller can tell that
   * case from a timeout or a cancel: a turn has a reply coming and the script
   * must let it finish before speaking again, while the other two do not.
   *
   * Optional because a headless host has no session to hear a turn on; the
   * runner degrades to a short pause rather than hanging when it is absent.
   */
  waitForUserTurnAsync?(
    sinceTurnCount: number,
    timeoutMs: number,
    signal: AbortSignal,
  ): Promise<boolean>;
  /**
   * The signed-in learner's first name, for the `<user name>` token a `say:`
   * line may carry. Empty when nothing is known.
   *
   * On the host rather than injected separately because this is the runner's
   * one seam to the session, and the name is a runtime fact it has no other way
   * to reach — the same reason `getUserTurnCount` lives here. Optional so a
   * headless host degrades to leaving the token unresolved-to-empty rather than
   * failing.
   */
  getUserFirstName?(): string;
  /**
   * Cuts off whatever Spark is saying right now (barge-in), whether that is a
   * script's say line or an answer to a question the learner asked.
   *
   * A say line has no handle once sent — `sayVerbatim` posts a user message and
   * the model speaks the reply — so the only way to silence one is to cancel
   * the RESPONSE. Doing that also un-parks {@link waitForResponseIdleAsync},
   * which is what lets a cancelled script unwind (and revert) in the moment
   * instead of at its 60-second say timeout.
   */
  stopSpeaking(): void;
}

/** How a script is being played; every field is off by default. */
export interface WorkflowScriptPlayOptions {
  /**
   * The run is REPLAYING the step this script belongs to, which the run model
   * reverted first (`revertStepChanges`). Each `Toggle_Model_Animation` step
   * then plays its take from the start even when its write changes nothing
   * (AI-1459).
   */
  readonly isStepReplay?: boolean;
}

export type WorkflowScriptPlaybackStatus = "playing" | "finished" | "stopped" | "failed";

export interface WorkflowScriptPlaybackState {
  readonly actionId: string;
  readonly scriptId: string;
  readonly stepName: string;
  readonly trigger: string;
  /** 1-based index of the executing step (0 before the first step). */
  readonly stepIndex: number;
  readonly stepCount: number;
  readonly stepLabel: string;
  readonly status: WorkflowScriptPlaybackStatus;
  /**
   * This emit is a `wait_for` step parked on the LEARNER's next turn: the
   * lesson has stopped and handed them the floor.
   *
   * True on exactly one emit — that step's `playing` — and false on every
   * other, including its own script's terminal one. A consumer that needs to
   * know the wait is OVER reads the next `playing` or the terminal emit rather
   * than a second signal here.
   *
   * It exists because the shell CAN hide Spark's composer while a step's script
   * plays (`WorkflowPlaybackView.isAiInputHeld`), and this is the one moment in
   * a script where that would be a dead end: the script would be waiting for a
   * reply on a surface the learner cannot see. That hide is gated OFF as
   * shipped (2026-09-01), so today this signal releases a latch nobody is
   * acting on — it stays wired because the flag is one line, and because
   * turning it back on with the escape missing is the one way to strand a
   * learner outright.
   */
  readonly isAwaitingUserResponse: boolean;
}

/** Deterministic replay id for a workflow script (`wf:<workflowUid>:<scriptId>`). */
export function buildScriptActionId(
  document: WorkflowDocument | null,
  script: WorkflowScript | null,
): string {
  const workflowKey = document?.uid?.trim() ? document.uid.trim() : "workflow";
  return `${K_ACTION_ID_PREFIX}${workflowKey}:${script?.id?.trim() ?? ""}`;
}

/** True when an action id belongs to a workflow script. */
export function isWorkflowScriptActionId(actionId: string | null | undefined): boolean {
  return !!actionId && actionId.startsWith(K_ACTION_ID_PREFIX);
}

/** Extracts the script id back out of a workflow-script action id, or null. */
export function tryGetScriptIdFromActionId(actionId: string | null | undefined): string | null {
  if (!actionId || !isWorkflowScriptActionId(actionId)) return null;
  const lastSeparator = actionId.lastIndexOf(":");
  return lastSeparator > 0 && lastSeparator + 1 < actionId.length
    ? actionId.substring(lastSeparator + 1)
    : null;
}

interface ActiveScriptRun {
  cancelled: boolean;
  /**
   * Tool steps already dispatched and not yet settled. A revert must not run
   * while one is outstanding: its write has not landed, so restoring first
   * would leave behind exactly the change the revert exists to undo.
   */
  pendingToolCount: number;
  /** Exact tasks behind pendingToolCount; teardown waits for every late write. */
  readonly pendingToolTasks: Set<Promise<void>>;
  readonly revertMode: WorkflowScriptRevert;
  readonly ledger: WorkflowRevertLedger | null;
  /** Idempotence for the two places that can settle it — a stop, and the loop. */
  revertSettled: boolean;
  /**
   * Resolves once this run has handed its captured state to the immediate
   * restore chain or the pending scene-change ledger. Project unload waits on
   * this when a tool write was already in flight at teardown.
   */
  readonly revertSettlement: Promise<void>;
  readonly resolveRevertSettlement: () => void;
}

export class WorkflowScriptRunner {
  /** Progress feed for the Workflow panel (per-step + lifecycle transitions). */
  readonly onScriptStateChanged = new Emitter<WorkflowScriptPlaybackState>();

  private readonly m_host: WorkflowScriptHost;
  private readonly m_activeScripts = new Map<string, ActiveScriptRun>();
  /** Includes cancelled runs whose in-flight tool has not handed over its ledger yet. */
  private readonly m_unsettledRuns = new Set<ActiveScriptRun>();
  private readonly m_revert: WorkflowRevertDependencies | null;
  /** `revert: on_scene_change` bags, waiting for their scene to be left. */
  private readonly m_pendingSceneChangeReverts = new WorkflowRevertLedger();
  /** `revert: none` lasts through the run, then returns to the authored baseline. */
  private readonly m_pendingPlaybackEndReverts = new WorkflowRevertLedger();
  /**
   * What SPARK itself changed on the current step of a run — a learner asking
   * it to hide, isolate or highlight something (AI-1424). Captured before each
   * of its calls ({@link beginLiveToolCall}) and put back on every step move
   * ({@link revertLiveChanges}), BEFORE the outgoing script's own revert:
   * Spark's changes sit on top of the step's authored state, so they come off
   * first. No view state — the step being arrived at owns the camera.
   */
  private readonly m_liveChanges = new WorkflowRevertLedger();
  /**
   * Spark's changes on a scene a step move is LEAVING, held back until that
   * scene is actually left (AI-1429) — exactly when an `on_scene_change`
   * script revert is drained ({@link handleActiveSceneChanged}).
   *
   * Undone at the start of the move instead, they were visible: the project
   * keeps the old scene on screen while it loads the next one, so a learner
   * who had asked Spark to isolate a part watched the whole model come back
   * for the length of the load before the new scene replaced it. Undone after
   * the switch, they land on a scene the renderer has stopped drawing.
   *
   * Emptied three ways, so nothing is ever left behind: the switch
   * ({@link handleActiveSceneChanged}), the step's scene activation settling
   * without one ({@link settleLiveChangesAwaitingSceneLeave}), and any
   * immediate {@link revertLiveChanges} — a later move, or the run ending.
   */
  private readonly m_liveChangesAwaitingSceneLeave = new WorkflowRevertLedger();
  /**
   * What the CURRENT step's own scripts changed, as of the moment the learner
   * arrived on it — so a replay can put the step back before playing it again
   * (AI-1459).
   *
   * A second record of captures the step's scripts already make, not a new
   * kind of revert: each also goes to its declared-mode ledger as before, and
   * only one of the two is ever restored. A replay restores this one; every
   * other arrival drops it, because a move that leaves the step leaves its
   * changes to their declared mode.
   *
   * Without it a replay could not revert at all. An `on_scene_change` script's
   * changes wait for a scene change that a replay never makes, and a `none`
   * script's for the end of the run — so the step replayed on top of its own
   * end state, and the one effect that is a no-op when re-applied, a take that
   * had finished, did not play again. Neither bag can stand in for this: both
   * also hold EARLIER steps' changes on the same scene, which this step
   * arrived to find and must keep.
   *
   * `on_action_finish` scripts are left out: they put their own changes back
   * when they end, including when the replay stops one mid-flight.
   */
  private m_stepChanges = new WorkflowRevertLedger();
  /**
   * The animation each scene's models had when the RUN arrived on that scene:
   * the first `Toggle_Model_Animation` capture there, from any script in any
   * revert mode, since the scene was last entered. Put back when the run leaves
   * the scene ({@link restoreSceneEntryAnimation}), so the next arrival finds
   * what the first one found.
   *
   * Without it a step that started a take left the scene's model set to
   * autoplay it, and the renderer restarts an autoplaying clip whenever its
   * scene comes back on screen — so going BACK to that step played the take
   * on arrival, under the narration, and then the step's script played it
   * again (Broaching Cell, 2026-10-06). `revert: none` bags cannot stand in for
   * this: they keep the first capture of the whole run and are only drained
   * at its end.
   *
   * Copies, so restoring one never empties the declared-mode bags; their own
   * restores skip whatever this has already put back.
   */
  private readonly m_sceneEntryAnimation = new WorkflowRevertLedger();
  /** Spark tool calls started and not yet finished (begin/end pairs from the executor). */
  private m_liveToolsInFlight = 0;
  private readonly m_liveToolsIdleWaiters: Array<() => void> = [];
  /**
   * Every restore runs on one chain, and a script waits for it before its
   * first step. Without that ordering a Next that stops a reverting script and
   * lands on a step whose on-start script captures immediately would snapshot
   * the PREVIOUS script's changes as the state to preserve.
   */
  private m_revertChain: Promise<void> = Promise.resolve();
  private m_playbackScopeRelease: (() => void) | null = null;
  private m_finishPlaybackPromise: Promise<void> | null = null;
  private m_isRunPlaybackActive = false;
  private m_standalonePlaybackCount = 0;
  private m_lastActiveSceneId: string | null = null;
  private readonly m_offFns: Array<() => void> = [];

  /**
   * @param revert Scene state needed to capture and restore script changes.
   * Optional so a headless host (tests, the MCP bridge) can run scripts
   * without a project; scripts then play with revert disabled.
   */
  constructor(host: WorkflowScriptHost, revert: WorkflowRevertDependencies | null = null) {
    this.m_host = host;
    this.m_revert = revert;
    if (!revert) return;

    this.m_lastActiveSceneId = revert.project.activeSceneId;
    this.m_offFns.push(
      revert.project.onActiveSceneChanged.on((scene) => this.handleActiveSceneChanged(scene?.id ?? null)),
      revert.project.registerProjectUnloadBarrier(() => this.prepareProjectUnloadAsync()),
    );
  }

  /**
   * Opens the project model's transient mutation boundary for a complete
   * Workflow run. Starting waits for prior cleanup so a new run can never
   * capture the previous run's transient state as its authored baseline.
   */
  async beginWorkflowPlaybackAsync(): Promise<void> {
    if (this.m_finishPlaybackPromise) await this.m_finishPlaybackPromise;
    if (this.m_isRunPlaybackActive) return;
    // Starting a full run supersedes any editor script previews still playing.
    if (this.m_standalonePlaybackCount > 0) await this.finishWorkflowPlaybackAsync();
    this.m_isRunPlaybackActive = true;
    this.ensurePlaybackMutationScope();
  }

  /**
   * Stops scripts, drains every declared-mode and playback-end revert, then
   * releases persistence. The scope remains active throughout asynchronous
   * restoration, so putting the authored state back never writes it again.
   */
  async finishWorkflowPlaybackAsync(): Promise<void> {
    if (this.m_finishPlaybackPromise) return this.m_finishPlaybackPromise;
    if (
      !this.m_playbackScopeRelease
      && !this.m_isRunPlaybackActive
      && this.m_standalonePlaybackCount === 0
      && this.m_activeScripts.size === 0
      && this.m_unsettledRuns.size === 0
      && !this.m_pendingSceneChangeReverts.hasRestorableState
      && !this.m_pendingPlaybackEndReverts.hasRestorableState
      && !this.m_liveChanges.hasRestorableState
      && !this.m_liveChangesAwaitingSceneLeave.hasRestorableState
    ) return;

    this.m_isRunPlaybackActive = false;
    const finish = this.finishWorkflowPlaybackCoreAsync().finally(() => {
      if (this.m_finishPlaybackPromise === finish) this.m_finishPlaybackPromise = null;
    });
    this.m_finishPlaybackPromise = finish;
    return finish;
  }

  private async finishWorkflowPlaybackCoreAsync(): Promise<void> {
    const activeRuns = [...this.m_unsettledRuns];
    // Spark's changes first, as on a step move: they sit on top of whatever
    // the scripts left. Normally already emptied by the stop's own
    // `interruptPlayback`; this is what covers a project unload.
    this.revertLiveChanges();
    this.stopAllRunningScripts();
    // Every row in it is also in a declared-mode bag drained below.
    this.discardStepChanges();
    // Copies of declared-mode captures too, and those bags are about to put
    // everything back — this record has nothing left to do.
    this.m_sceneEntryAnimation.takeAll();
    this.interruptSpeech();

    try {
      await Promise.all(activeRuns.map((run) => run.revertSettlement));
      await this.m_revertChain;

      const finalRestore = new WorkflowRevertLedger();
      for (const state of this.m_pendingSceneChangeReverts.takeAll()) {
        finalRestore.merge(state);
      }
      for (const state of this.m_pendingPlaybackEndReverts.takeAll()) {
        finalRestore.merge(state);
      }
      if (finalRestore.hasRestorableState) {
        this.queueRevert((deps) => finalRestore.restoreAllAsync(deps));
        await this.m_revertChain;
      }
    } finally {
      this.m_standalonePlaybackCount = 0;
      const release = this.m_playbackScopeRelease;
      this.m_playbackScopeRelease = null;
      release?.();
    }
  }

  /**
   * True while a guided RUN is playing and its changes can be undone — the
   * window in which Spark's own calls are captured by {@link beginLiveToolCall}.
   *
   * `AIGuardrailsModel` lets the step-scoped tools through on exactly this, so
   * "Spark may do it" and "the run will undo it" cannot disagree. Not true for
   * the editor's single-script preview: that is not a run, and it has no step
   * to change.
   */
  get isCapturingLiveChanges(): boolean {
    return this.m_isRunPlaybackActive && this.m_revert !== null;
  }

  /**
   * Called just before SPARK (not a script) runs a tool, from the executor's
   * start event — the last moment the pre-tool state is still readable.
   *
   * Only for the calls that can change the scene during a run
   * ({@link isRunUndoableTool}); everything else — a lookup, a document search
   * — is ignored, so a slow read in flight cannot hold up a step move's undo.
   * Counts such calls whether or not a run is playing, so the pairing with
   * {@link endLiveToolCall} stays exact across a run starting mid-call;
   * captures only during a run.
   */
  beginLiveToolCall(toolName: string, args: Readonly<Record<string, unknown>>): void {
    if (!WorkflowScriptRunner.isRunUndoableTool(toolName)) return;
    this.m_liveToolsInFlight++;
    if (!this.isCapturingLiveChanges || !this.m_revert) return;
    this.m_liveChanges.capture(toolName, args, this.m_revert, { includeViewState: false });
  }

  /** The completion half of {@link beginLiveToolCall}. */
  endLiveToolCall(toolName: string): void {
    if (!WorkflowScriptRunner.isRunUndoableTool(toolName)) return;
    this.m_liveToolsInFlight = Math.max(0, this.m_liveToolsInFlight - 1);
    if (this.m_liveToolsInFlight > 0) return;
    for (const resolve of this.m_liveToolsIdleWaiters.splice(0)) resolve();
  }

  /**
   * Puts back everything Spark changed on the step being left (AI-1424: a
   * learner who asked it to isolate a part came back to the step still
   * isolated). Call on every step move BEFORE {@link stopAllRunningScripts},
   * so Spark's changes come off before the outgoing script's own revert —
   * they were made on top of it.
   *
   * Queued on the revert chain, which the next step's script awaits before its
   * first capture, so that script can never take Spark's leftovers as its
   * starting state. The restore first waits for Spark's calls still running:
   * one that landed after the undo would leave its change behind.
   *
   * @param leavingSceneId The scene the move is about to LEAVE, when the step
   * being arrived at is on a different one (AI-1429). Spark's changes on that
   * scene are then held back until it is actually left — see
   * {@link m_liveChangesAwaitingSceneLeave} — instead of coming off on screen
   * while the next scene loads. Everything else comes off now. Null (a move
   * that stays in the scene, a replay, the run ending) undoes everything now,
   * including anything an earlier move held back for a scene it never left.
   */
  revertLiveChanges(leavingSceneId: string | null = null): void {
    if (!this.m_revert) return;
    const changes = new WorkflowRevertLedger();
    if (leavingSceneId !== null) {
      const departing = this.m_liveChanges.take(leavingSceneId);
      if (departing) this.m_liveChangesAwaitingSceneLeave.merge(departing);
    } else {
      // Held-back captures FIRST: merging is first-wins, and they are the
      // earlier "before" for any row Spark has changed again since.
      for (const state of this.m_liveChangesAwaitingSceneLeave.takeAll()) changes.merge(state);
    }
    for (const state of this.m_liveChanges.takeAll()) changes.merge(state);
    if (!changes.hasRestorableState) return;
    this.queueLiveChangesRestore(changes);
  }

  /**
   * Called once a step's scene activation has settled — the scene switch done,
   * or never started. Whatever Spark changed on a scene the move meant to
   * leave, but did not (a stale scene reference, a switch superseded), comes
   * off now, on the scene still in front of the learner: that IS where they
   * are, so the undo is the step change, not a glitch. After a real switch
   * there is nothing left here — {@link handleActiveSceneChanged} took it.
   */
  settleLiveChangesAwaitingSceneLeave(): void {
    if (!this.m_revert || !this.m_liveChangesAwaitingSceneLeave.hasRestorableState) return;
    const held = new WorkflowRevertLedger();
    for (const state of this.m_liveChangesAwaitingSceneLeave.takeAll()) held.merge(state);
    this.queueLiveChangesRestore(held);
  }

  /** Spark's undo, on the revert chain, after Spark's own calls in flight land. */
  private queueLiveChangesRestore(changes: WorkflowRevertLedger): void {
    this.queueRevert(async (deps) => {
      await this.waitForLiveToolsIdleAsync();
      await changes.restoreAllAsync(deps, { includeViewState: false });
    });
  }

  /**
   * The tools through which Spark can change the scene while a run plays: the
   * step-scoped writer tools the guardrail lets through
   * (`K_STEP_SCOPED_TOOL_IDS`), plus `focus_on_part` as a defence. Its
   * `useIsolate` fade is refused during a run (`EntitiesExecutor.focusOnPart`
   * asks `authorizeProjectWrite`), so today it changes nothing here; it stays
   * listed because the tool is not `writerOnly`, and if that refusal is ever
   * relaxed the fade must already be undone at the step change. Every other
   * writer tool is refused during playback.
   *
   * By NAME, not by arguments: the completion event carries no arguments, and
   * begin and end must agree on which calls they count.
   */
  private static isRunUndoableTool(toolName: string): boolean {
    return (
      isStepScopedTool(toolName)
      || toolName.trim().toLowerCase() === AgentToolNames.FocusOnPart.toLowerCase()
    );
  }

  private waitForLiveToolsIdleAsync(): Promise<void> {
    if (this.m_liveToolsInFlight === 0) return Promise.resolve();
    return new Promise<void>((resolve) => {
      const timer = window.setTimeout(() => {
        const index = this.m_liveToolsIdleWaiters.indexOf(settle);
        if (index >= 0) this.m_liveToolsIdleWaiters.splice(index, 1);
        console.warn("[WorkflowScriptRunner] Spark tool still running at step change; reverting anyway.");
        resolve();
      }, K_LIVE_TOOL_SETTLE_TIMEOUT_MS);
      const settle = (): void => {
        window.clearTimeout(timer);
        resolve();
      };
      this.m_liveToolsIdleWaiters.push(settle);
    });
  }

  /**
   * The step's own script outranks Spark on anything they both touch: before a
   * script tool step writes, whatever it is about to write that Spark had
   * changed goes back to its pre-Spark value and leaves Spark's record.
   *
   * Otherwise the script would capture SPARK's value as its "before", and its
   * own revert would put Spark's change back after the step move had removed
   * it. Restored rather than merely handed over because capture is
   * row-granular: a script hiding a part Spark highlighted writes visibility
   * only, and the highlight must still come off.
   */
  private async yieldLiveChangesToScriptStepAsync(
    toolId: string,
    args: Readonly<Record<string, unknown>>,
  ): Promise<void> {
    if (!this.m_revert || !this.m_liveChanges.hasRestorableState) return;
    const probe = captureToolRevertState(toolId, args, this.m_revert, { includeViewState: false });
    if (!probe) return;
    const overlap = this.m_liveChanges.takeOverlapping(probe);
    if (!overlap.hasRestorableState) return;
    this.queueRevert((deps) => overlap.restoreAllAsync(deps, { includeViewState: false }));
    await this.m_revertChain;
  }

  dispose(): void {
    void this.finishWorkflowPlaybackAsync();
    for (const off of this.m_offFns) off();
    this.m_offFns.length = 0;
  }

  get hasRunningScripts(): boolean {
    return this.m_activeScripts.size > 0;
  }

  isScriptRunning(actionId: string): boolean {
    return this.m_activeScripts.has(actionId);
  }

  /** Stops every workflow script currently playing (play-mode exit, run stop). */
  stopAllRunningScripts(): void {
    for (const run of this.m_activeScripts.values()) {
      run.cancelled = true;

      // Revert NOW rather than when the loop unwinds. The loop's `finally` is
      // still the normal path, but it cannot run while the script is parked on
      // a say — up to 60 s — and by then the learner is two steps away with
      // the previous script's changes still applied to their scene.
      //
      // Unless a tool step is in flight: that write has not landed yet, so a
      // restore would be overwritten by the very change it is undoing. Those
      // runs settle in the `finally`, which is one tool call away.
      if (run.pendingToolCount === 0) this.settleRunRevert(run);
    }

    // Cancelled runs stop counting as "running" IMMEDIATELY, not when their
    // in-flight step await unwinds (a pending say can hold that for up to
    // 60 s). A zombie entry here made Next's skip-branch fire on a first
    // press, and made a re-entered step's on-start replay refuse with
    // `script_already_playing`. The run objects keep their cancelled flag —
    // each playScriptAsync loop holds a direct reference.
    this.m_activeScripts.clear();
  }

  /**
   * Puts back what the current step's own scripts changed — the step as the
   * learner arrived to find it — ahead of a REPLAY of that step (AI-1459).
   * Call after {@link revertLiveChanges} and {@link stopAllRunningScripts}:
   * Spark's changes were made on top of the step's, and a stopped script's
   * captures must be final before this record is taken.
   *
   * Queued on the revert chain, which the replayed script awaits before its
   * first capture, so the replay captures the arrival state again rather than
   * the end state it is replacing. A stopped script's tool call that is still
   * landing is waited for first: restoring under it would leave its change on
   * screen.
   *
   * Without view state, like every restore a step move makes: the camera
   * belongs to the step's opening framing, which the replay applies itself.
   * Animation comes back as a POSE as well as settings (`WorkflowRevertState`),
   * so a take the step arrived to find finished is held there, not played.
   */
  revertStepChanges(): void {
    const changes = this.m_stepChanges;
    this.m_stepChanges = new WorkflowRevertLedger();
    if (!this.m_revert || !changes.hasRestorableState) return;
    const landing = [...this.m_unsettledRuns].map((run) => run.revertSettlement);
    this.queueRevert(async (deps) => {
      await Promise.all(landing);
      await changes.restoreAllAsync(deps, { includeViewState: false });
    });
  }

  /**
   * Forgets the current step's record without restoring it: the step is being
   * LEFT (or the run is ending), and what its scripts changed now belongs to
   * their declared revert mode, exactly as before AI-1459.
   */
  discardStepChanges(): void {
    this.m_stepChanges = new WorkflowRevertLedger();
  }

  /**
   * Stops script work and restores every queued `on_scene_change` bag before
   * the project model drops its scene/entity state.
   *
   * The normal `onActiveSceneChanged(null)` notification is too late for this:
   * project unload has already cleared the maps the restore reads. The project
   * model awaits this method through its unload-barrier seam while all captured
   * rows and the cloud write context are still available.
   */
  private async prepareProjectUnloadAsync(): Promise<void> {
    await this.finishWorkflowPlaybackAsync();
  }

  /**
   * Silences Spark mid-sentence. Separate from {@link stopAllRunningScripts}
   * because the two answer different questions: a step change must stop the
   * outgoing narration whether or not a SCRIPT was the thing narrating — the
   * learner may simply have asked a question and moved on while it was being
   * answered.
   */
  interruptSpeech(): void {
    try {
      this.m_host.stopSpeaking();
    } catch (error) {
      // Barge-in is best-effort: a disconnected session must not break a move.
      console.warn("[WorkflowScriptRunner] Could not stop Spark speaking:", error);
    }
  }

  /**
   * Executes a script's steps in order. Returns an "ok: ..." / "error: ..." result string
   * following the tool-result convention (strings match Unity's `PlayScriptAsync`).
   */
  async playScriptAsync(
    document: WorkflowDocument | null,
    step: WorkflowStep | null,
    script: WorkflowScript | null,
    options: WorkflowScriptPlayOptions = {},
  ): Promise<string> {
    // Authoring preview calls the runner outside a full Workflow run. Give it
    // the same transient boundary and restore-at-playback-end behavior.
    const standalonePlayback = await this.beginStandalonePlaybackAsync();
    try {
      return await this.playScriptInScopeAsync(document, step, script, options);
    } finally {
      if (standalonePlayback) {
        this.m_standalonePlaybackCount = Math.max(0, this.m_standalonePlaybackCount - 1);
        if (this.m_standalonePlaybackCount === 0) {
          await this.finishWorkflowPlaybackAsync();
        }
      }
    }
  }

  private async beginStandalonePlaybackAsync(): Promise<boolean> {
    if (this.m_finishPlaybackPromise) await this.m_finishPlaybackPromise;
    if (this.m_isRunPlaybackActive) return false;
    this.m_standalonePlaybackCount++;
    this.ensurePlaybackMutationScope();
    return true;
  }

  private ensurePlaybackMutationScope(): void {
    if (this.m_playbackScopeRelease || !this.m_revert) return;
    this.m_playbackScopeRelease =
      this.m_revert.project.beginWorkflowPlaybackMutationScope();
  }

  private async playScriptInScopeAsync(
    document: WorkflowDocument | null,
    step: WorkflowStep | null,
    script: WorkflowScript | null,
    options: WorkflowScriptPlayOptions,
  ): Promise<string> {
    if (!document || !step || !script) {
      return "error: script_unavailable";
    }

    // Validate every step up-front (Unity `TryBuildActionData` fails before playing).
    const validationError = WorkflowScriptRunner.validateSteps(script);
    if (validationError) return validationError;

    const actionId = buildScriptActionId(document, script);
    if (this.m_activeScripts.has(actionId)) {
      return `ok: script_already_playing scriptId=${script.id}`;
    }

    const revertMode = this.resolveRevertMode(script);
    // `none` captures directly into the playback-wide ledger, in execution
    // order. Deferring that merge until concurrent scripts settle would make
    // promise-completion order decide which "before" value wins.
    const ledger =
      this.m_revert && revertMode !== "none" ? new WorkflowRevertLedger() : null;
    // A revert still settling from the script this one replaces has to land
    // before the first capture, or it would be captured as the "before" state.
    // Every mode, not only those with a ledger of their own: a `none` script
    // captures into the playback-end bag and the step's record, and a replay's
    // restore landing after its first tool would undo that tool (AI-1459).
    if (this.m_revert) await this.m_revertChain;

    let resolveRevertSettlement = (): void => {};
    const revertSettlement = new Promise<void>((resolve) => {
      resolveRevertSettlement = resolve;
    });
    const run: ActiveScriptRun = {
      cancelled: false,
      pendingToolCount: 0,
      pendingToolTasks: new Set<Promise<void>>(),
      revertMode,
      ledger,
      revertSettled: false,
      revertSettlement,
      resolveRevertSettlement,
    };
    this.m_unsettledRuns.add(run);
    this.m_activeScripts.set(actionId, run);
    const stepCount = script.steps.length;
    // The loop below shadows `step` with the SCRIPT step it is executing, so
    // the owning WORKFLOW step — which `<step_instructions>` resolves against —
    // is captured here under its own name.
    const owningStep = step;
    const overlappedTasks: Promise<void>[] = [];
    let activeSpeechTask: Promise<void> | null = null;
    const emit = (
      stepIndex: number,
      stepLabel: string,
      status: WorkflowScriptPlaybackStatus,
      isAwaitingUserResponse = false,
    ) =>
      this.onScriptStateChanged.emit({
        actionId,
        scriptId: script.id,
        stepName: step.name,
        trigger: script.trigger,
        stepIndex,
        stepCount,
        stepLabel,
        status,
        isAwaitingUserResponse,
      });

    emit(0, "", "playing");
    try {
      // Two step-boundary snapshots of the turn counter: a `wait_for` asks
      // about the one from before the step that PRECEDED it, which is the
      // window in which its question was being spoken.
      let turnCountBeforePreviousStep = this.readUserTurnCount();
      let turnCountBeforeThisStep = turnCountBeforePreviousStep;

      for (let i = 0; i < script.steps.length; i++) {
        turnCountBeforePreviousStep = turnCountBeforeThisStep;
        turnCountBeforeThisStep = this.readUserTurnCount();

        if (run.cancelled) {
          emit(i, "", "stopped");
          return `ok: script_stopped scriptId=${script.id}`;
        }

        const step = script.steps[i];
        const stepNumber = i + 1;
        if (WorkflowScriptRunner.isDoNotWaitStep(step)) continue;

        const shouldOverlap = WorkflowScriptRunner.shouldOverlapStep(script, i);
        if (step.isSay) {
          // Three hops, and the position of each is load-bearing.
          //
          // 1. `<step_instructions>` defers to the OWNING step's authored copy,
          //    so the panel and the spoken line cannot drift. Resolved here
          //    rather than at parse time: the author can edit the instructions
          //    after the script is written, and a value baked in at parse would
          //    be the stale copy the token exists to prevent.
          // 2. Those instructions are MARKDOWN, and the line reaches the model
          //    under a contract that says to speak it VERBATIM — so the markers
          //    have to come off before it is handed over, or they are read out
          //    ("asterix, asterix, part"; `markdownSpeech.ts` carries the whole
          //    argument, including why this is not a prompt rule). It runs on a
          //    literal `say:` line too: an author typing `**this**` into one
          //    means the same thing by it.
          // 3. Runtime tokens run LAST, and for two separate reasons. The
          //    instructions are themselves authored prose, so a `<user name>`
          //    written in them has to be substituted too — running this before
          //    hop 1 would only ever see the token's brackets. And running it
          //    before hop 2 would put a markup parser over the learner's own
          //    NAME, which is data rather than authored text.
          const sayText = resolveWorkflowText(
            markdownToSpeechText(resolveSayText(step.sayText, owningStep)),
            { userFirstName: this.m_host.getUserFirstName?.() ?? "" },
          );
          // Tested on the RESULT, not on the source. Instructions the step does
          // not have resolve to nothing — and so does a line that was only
          // markup, a `---` rule being the reachable case. An empty
          // `INTERNAL SAY:` would leave the model improvising a line nobody
          // wrote, so skip the step instead; validate() warns about the first
          // case, so the author is told rather than the run silently narrating.
          if (sayText.trim().length === 0) continue;

          // Unity never overlaps two SAY steps with each other, even when the
          // first SAY overlaps intervening non-speech work.
          if (activeSpeechTask) await activeSpeechTask;
          emit(stepNumber, `Say "${sayText}"`, "playing");
          const speechTask = this.playSayStepAsync(run, sayText);
          activeSpeechTask = speechTask;
          const trackedSpeechTask = speechTask.finally(() => {
            if (activeSpeechTask === speechTask) activeSpeechTask = null;
          });
          if (shouldOverlap) overlappedTasks.push(trackedSpeechTask);
          else await trackedSpeechTask;
          continue;
        }

        const toolId = K_CANONICAL_TOOL_IDS.get(step.tool.trim().toLowerCase()) ?? step.tool;

        // `wait_for` is the runner's own command, not a registered tool: it
        // pauses the LESSON, which is a notion playback has and a chat turn
        // does not. Handled here for the same reason `do_not_wait` is —
        // dispatching it to the executor would answer `tool_not_supported`.
        //
        // A pending say is awaited first: waiting for the learner while Spark
        // is still mid-sentence would ask them to answer a question they have
        // not heard the end of.
        if (WorkflowScriptRunner.isWaitForStep(step)) {
          const waitArgs = step.args ?? {};
          emit(
            stepNumber,
            WorkflowScriptRunner.describeWait(waitArgs),
            "playing",
            // A `delay` wait is still the lesson running; only a wait on the
            // LEARNER hands them the floor, which is what the shell reads to
            // bring Spark's composer back mid-script.
            WorkflowScriptRunner.readWaitTrigger(waitArgs) === "user_response",
          );

          // Waits past the count from BEFORE the previous step, so a learner
          // who answered over the question the previous `say` was asking has
          // already satisfied it. Attaching a listener here — however early —
          // cannot see that turn: the say was awaited in its own iteration.
          const waitTask = this.playWaitStepAsync(run, waitArgs, turnCountBeforePreviousStep);
          if (activeSpeechTask) await activeSpeechTask;

          // `do_not_wait` applies to whatever step precedes it, with no
          // carve-out for this one — the reference says so, and a script author
          // has a real use for it ("ask them to look, and highlight it while
          // they do"). Honoured the same way the say and tool branches do.
          if (shouldOverlap) overlappedTasks.push(waitTask);
          else await waitTask;
          continue;
        }

        // `play_video` is narrower than the Guide's live `control_media` tool:
        // it can only start the video belonging to this script's own workflow
        // step. The media element mounts asynchronously after step activation,
        // so the runner owns the short readiness wait instead of turning a
        // routine first-render race into a failed command.
        if (WorkflowScriptRunner.isPlayVideoStep(step)) {
          emit(stepNumber, "Play video", "playing");
          const playTask = this.playVideoStepAsync(run, owningStep.name);
          if (shouldOverlap) overlappedTasks.push(playTask);
          else await playTask;
          continue;
        }

        emit(stepNumber, `${stepNumber}. ${toolId}`, "playing");
        // Steps are authored as YAML, so arguments arrive as a real object —
        // no JSON string to re-parse (and no parse failure to handle) here.
        const args = step.args ?? {};
        // Before the tool runs: what it is about to overwrite is the only
        // moment the pre-script state is still readable.
        if (this.m_revert) {
          // …and before that capture, the script takes back anything Spark
          // changed that this step writes, or the capture would record
          // Spark's value as the "before".
          if (this.m_liveChanges.hasRestorableState) {
            await this.yieldLiveChangesToScriptStepAsync(toolId, args);
            if (run.cancelled) {
              emit(i, "", "stopped");
              return `ok: script_stopped scriptId=${script.id}`;
            }
          }
          const captureLedger =
            revertMode === "none" ? this.m_pendingPlaybackEndReverts : ledger;
          const captured = captureToolRevertState(toolId, args, this.m_revert);
          if (captured?.hasRestorableState) {
            captureLedger?.merge(captured);
            // The step's own record, for a replay (AI-1459). A RUN's scripts
            // only — an editor preview has no step to replay — and not a
            // cancelled one, which belongs to the step being left.
            if (this.m_isRunPlaybackActive && revertMode !== "on_action_finish" && !run.cancelled) {
              this.m_stepChanges.mergeCopy(captured);
            }
            // …and the scene's arrival animation, whatever the mode: an
            // `on_action_finish` script that touched it first saw the arrival
            // value too.
            if (this.m_isRunPlaybackActive) {
              const animation = captured.copyAnimation();
              if (animation.hasRestorableState) this.m_sceneEntryAnimation.merge(animation);
            }
          }
        }
        const toolTask = trackToolStep(run, async () => {
          const succeeded = await this.playToolStepAsync(script.id, stepNumber, toolId, args);
          if (succeeded && options.isStepReplay && !run.cancelled) {
            this.restartReplayedAnimations(toolId, args);
          }
        });
        if (shouldOverlap) overlappedTasks.push(toolTask);
        else await toolTask;
      }

      // `do_not_wait` overlaps a step with later work, but the script itself
      // remains active until every overlapped step has settled.
      if (overlappedTasks.length > 0) {
        await Promise.all(overlappedTasks);
      }

      // A cancel that lands while the FINAL step is awaiting exits the loop
      // without hitting the top-of-loop check — it must still terminate as
      // "stopped", never "finished": a cancelled run must not report the
      // same terminal state as one that played to the end,
      // and a stale finish from a cancelled script would complete-and-advance
      // a step the learner deliberately navigated away from (or back onto).
      if (run.cancelled) {
        emit(stepCount, "", "stopped");
        return `ok: script_stopped scriptId=${script.id}`;
      }

      emit(stepCount, "", "finished");
      return `ok: script_played scriptId=${script.id} step="${step.name}" steps=${stepCount}`;
    } catch (e) {
      console.warn(`[WorkflowScriptRunner] Script '${script.id}' playback failed:`, e);
      emit(0, "", "failed");
      return `error: script_playback_failed scriptId=${script.id}`;
    } finally {
      // Settles however the script ended — finished, stopped mid-step by Next
      // or Back, or thrown. A script the learner walked out of is exactly the
      // one whose half-applied changes most need putting back. A stop may have
      // settled it already (see `stopAllRunningScripts`), which is why this
      // goes through the idempotent seam rather than calling `settleRevert`.
      this.settleRunRevertWhenToolsIdle(run);
      // Only remove the entry when it is still OURS: a cancelled run's map
      // slot may have been cleared and re-occupied by a restart of the same
      // script, and deleting blindly would evict the newer, live run.
      if (this.m_activeScripts.get(actionId) === run) {
        this.m_activeScripts.delete(actionId);
      }
    }
  }

  /**
   * Speaks one authored line, once the session can actually carry it.
   *
   * The wait is here rather than around the RUN because a say is the only
   * thing in a script that needs the model: a step's scene, media and
   * instructions should render the moment the learner arrives, and a workflow
   * with no say steps must not wait for an AI session at all.
   *
   * ⚠ Two things the wait makes newly possible, both handled below. The
   * learner can navigate away while we are parked, so the run is re-checked
   * before the line is sent — otherwise the step they just LEFT narrates over
   * the one they are on. And a say that was never sent has no response coming,
   * so waiting for one would burn the idle grace (5 s) per step for nothing;
   * that dead air is what the pre-fix behaviour spent on every dropped line.
   */
  private async playSayStepAsync(run: ActiveScriptRun, text: string): Promise<void> {
    const waitForSpeechReady = this.m_host.waitForSpeechReadyAsync;
    if (waitForSpeechReady) {
      let ready = false;
      await this.raceCancellation(run, async (signal) => {
        ready = await waitForSpeechReady.call(this.m_host, signal);
      });
      if (run.cancelled) return;
      if (!ready) {
        console.warn(
          `[WorkflowScriptRunner] Say skipped — no ready realtime session: "${text}"`,
        );
        return;
      }
    }
    this.m_host.sayVerbatim(text);
    await this.m_host.waitForResponseIdleAsync(K_SAY_TIMEOUT_MS);
  }

  /** Resolves true when the tool succeeded; a failure is logged and the script plays on. */
  private async playToolStepAsync(
    scriptId: string,
    stepNumber: number,
    toolId: string,
    args: Record<string, unknown>,
  ): Promise<boolean> {
    const endPerformanceOperation = beginWorkflowPerformanceOperation("workflow tool", {
      scriptId,
      stepNumber,
      toolId,
    });
    try {
      const result = await this.m_host.executeToolAsync(toolId, args);
      if (!WorkflowScriptRunner.isFailureResult(result)) {
        await this.m_host.waitForToolEffectsIdleAsync?.(toolId, args);
        return true;
      }

      console.warn(
        `[WorkflowScriptRunner] Script '${scriptId}' step ${stepNumber} (${toolId}) failed: ${result}`,
      );
      // Unity's replay engine keeps playing after a failed state; match that.
      return false;
    } finally {
      endPerformanceOperation();
    }
  }

  /**
   * A REPLAYED step's `Toggle_Model_Animation` plays its take from the start
   * (AI-1459), the half of "reverted and restarted" the revert cannot do on its
   * own.
   *
   * The revert put the model back as the step found it, and most of the time
   * that alone makes the tool's write a real change, which starts the take.
   * But a step can arrive to find its own take already set — the author chose
   * it in the inspector too, or the step before played the same one — and then
   * the tool writes settings the model already has. That is deliberately a
   * no-op (`Entities_Executor.toggleModelAnimation`), so the take stayed where
   * it was. Restarting after a write that DID change something is harmless:
   * the clip had not started yet.
   *
   * Replays only. A first arrival keeps the tool's own semantics, so a looping
   * take a run has kept playing across several steps does not jump back to its
   * start each time a step re-asserts it.
   */
  private restartReplayedAnimations(toolId: string, args: Readonly<Record<string, unknown>>): void {
    if (toolId !== AgentToolNames.ToggleModelAnimation || !this.m_revert) return;
    const playback = this.m_revert.animation?.();
    const sceneId = this.m_revert.project.activeSceneId;
    if (!playback || !sceneId) return;
    for (const entityId of resolveAnimationTargetEntityIds(args, this.m_revert)) {
      playback.restart(sceneId, entityId);
    }
  }

  private async playVideoStepAsync(run: ActiveScriptRun, stepName: string): Promise<void> {
    const play = this.m_host.playCurrentStepVideo;
    if (!play) {
      console.warn("[WorkflowScriptRunner] play_video skipped — this host has no media playback surface.");
      return;
    }

    const deadline = performance.now() + K_VIDEO_READY_TIMEOUT_MS;
    let outcome: WorkflowMediaControlResult["outcome"] = "unavailable";
    while (!run.cancelled) {
      const result = play(stepName);
      outcome = result.outcome;
      if (outcome === "ok") return;
      if (outcome !== "unavailable" || performance.now() >= deadline) break;
      await new Promise<void>((resolve) => window.setTimeout(resolve, K_CANCEL_POLL_MS));
    }

    if (!run.cancelled) {
      console.warn(
        `[WorkflowScriptRunner] play_video on step '${stepName}' could not start (${outcome}).`,
      );
    }
  }

  /**
   * The mode this script will actually run under. A declared mode with no
   * scene state to capture against (headless host) degrades to `none` rather
   * than promising a revert that cannot happen.
   */
  private resolveRevertMode(script: WorkflowScript): WorkflowScriptRevert {
    if (script.revert === "none") return "none";
    if (this.m_revert) return script.revert;
    console.warn(
      `[WorkflowScriptRunner] Script '${script.id}' declares revert '${script.revert}' but no project is attached — steps apply without revert.`,
    );
    return "none";
  }

  /**
   * `on_action_finish` restores now; `on_scene_change` hands the bag to the
   * pending queue, where it waits for the learner to leave the scene the
   * script ran in (Unity `QueuePendingSceneChangeRevert`).
   */
  /**
   * Settles ONE run's revert, at most once. Two paths reach it — a stop that
   * does not want to wait for the loop, and the loop's own `finally` — and a
   * double restore would put back state a NEWER script has since captured as
   * its own "before".
   */
  private settleRunRevert(run: ActiveScriptRun): void {
    if (run.revertSettled) return;
    run.revertSettled = true;
    try {
      // A run is only ever cancelled by a step move or the run ending, and
      // either way the camera now belongs to the step being arrived at (or to
      // the stop). Putting this script's captured camera back would land after
      // that step's framing and pull the learner off it — including from the
      // loop's `finally`, one tool call after the move.
      this.settleRevert(run.revertMode, run.ledger, { includeViewState: !run.cancelled });
    } finally {
      run.resolveRevertSettlement();
      this.m_unsettledRuns.delete(run);
    }
  }

  /**
   * A cancelled `do_not_wait` script can reach its loop `finally` while an
   * overlapped tool is still writing. Keep its settlement unresolved until
   * every dispatched tool lands, so playback teardown cannot restore and
   * release the persistence scope ahead of a late mutation.
   */
  private settleRunRevertWhenToolsIdle(run: ActiveScriptRun): void {
    if (run.pendingToolCount === 0) {
      this.settleRunRevert(run);
      return;
    }
    const pending = [...run.pendingToolTasks];
    void Promise.allSettled(pending).then(() => {
      this.settleRunRevertWhenToolsIdle(run);
    });
  }

  private settleRevert(
    mode: WorkflowScriptRevert,
    ledger: WorkflowRevertLedger | null,
    options: WorkflowRevertViewOptions = {},
  ): void {
    if (!ledger || !this.m_revert || !ledger.hasRestorableState) return;

    if (mode === "on_scene_change") {
      this.m_pendingSceneChangeReverts.mergeLedger(ledger);
      return;
    }
    // This restore rewrites every row the script captured, so Spark's claim on
    // any of them ends here: undoing it later, at the step move, would put back
    // the script's own change after the script had removed it. Dropped rather
    // than restored — the script's restore is about to overwrite them anyway.
    // Including a claim a scene-changing move is holding back (AI-1429): that
    // one would otherwise land AFTER this restore, when the scene is left.
    this.m_liveChanges.takeOverlapping(ledger);
    this.m_liveChangesAwaitingSceneLeave.takeOverlapping(ledger);
    this.queueRevert((deps) => ledger.restoreAllAsync(deps, options));
  }

  /** Serializes restores so two of them can never interleave their writes. */
  private queueRevert(restore: (deps: WorkflowRevertDependencies) => Promise<void>): void {
    const deps = this.m_revert;
    if (!deps) return;
    this.m_revertChain = this.m_revertChain
      .then(() => restore(deps))
      .catch((e) => {
        console.warn("[WorkflowScriptRunner] Script revert failed:", e);
      });
  }

  /**
   * Drains `on_scene_change` bags for the scene being LEFT (Unity does the same
   * from `ProjectModelSlim`'s active-scene switch). A null scene is a fallback
   * drain; normal project unload restores through the awaited barrier above,
   * before the project model clears the rows this state needs.
   */
  private handleActiveSceneChanged(nextSceneId: string | null): void {
    const departed = this.m_lastActiveSceneId;
    this.m_lastActiveSceneId = nextSceneId;

    if (nextSceneId === null) {
      this.settleLiveChangesAwaitingSceneLeave();
      const pending = this.m_pendingSceneChangeReverts;
      this.queueRevert((deps) => pending.restoreAllAsync(deps));
      return;
    }
    if (!departed || departed === nextSceneId) return;

    // Spark's changes on the scene just left, held back by the move so they
    // come off now, off screen (AI-1429) — and BEFORE the departed scene's
    // script bag below, because they were made on top of it.
    const held = this.m_liveChangesAwaitingSceneLeave.take(departed);
    if (held) {
      const heldLedger = new WorkflowRevertLedger();
      heldLedger.merge(held);
      this.queueLiveChangesRestore(heldLedger);
    }
    const state = this.m_pendingSceneChangeReverts.take(departed);
    if (state) this.queueRevert((deps) => state.restoreAsync(deps));
    this.restoreSceneEntryAnimation(departed);
  }

  /**
   * Gives the models on a scene the run has just LEFT the animation they had
   * when the run arrived there ({@link m_sceneEntryAnimation}).
   *
   * LAST of the scene-leave restores: Spark's held changes and the
   * `on_scene_change` bag each put back what was there before THEIR changes,
   * which for animation can be a take an earlier script on this scene set.
   *
   * Off screen by construction — the renderer ignores settings updates for a
   * scene that is not active, and `restoreAsync` restores a clip's pose only on
   * the active scene — so nothing visibly changes now. The renderer picks the
   * settings up when the scene is next shown, and holds a take that no longer
   * autoplays on its first frame until the arriving step's script starts it.
   *
   * Waits for any script the move STOPPED whose tool call is still landing:
   * a `Toggle_Model_Animation` arriving after this restore would set the take
   * playing again. Only stopped runs — a running script that has just changed
   * scene itself can wait on the revert chain, and waiting for it from inside
   * the chain would never finish.
   */
  private restoreSceneEntryAnimation(departedSceneId: string): void {
    const entry = this.m_sceneEntryAnimation.take(departedSceneId);
    if (!entry) return;
    const landing = [...this.m_unsettledRuns]
      .filter((run) => run.cancelled)
      .map((run) => run.revertSettlement);
    this.queueRevert(async (deps) => {
      await Promise.all(landing);
      await entry.restoreAsync(deps, { includeViewState: false });
    });
  }

  /** Port of the step conversion checks in Unity `TryBuildActionData` (same error strings). */
  private static validateSteps(script: WorkflowScript): string | null {
    if (script.steps.length === 0) {
      return `error: script_has_no_steps (script "${script.id}")`;
    }

    let stepNumber = 0;
    for (const step of script.steps) {
      stepNumber++;
      if (step.isSay) continue;

      // A step that is neither `say:` nor `tool:` is preserved verbatim by
      // the codec (`invalidRaw`) and reported by `validate()`; refuse to play
      // it with a step-numbered reason instead of `unknown_tool ""`.
      if (step.isInvalid || step.tool.trim().length === 0) {
        return `error: step_is_not_a_say_or_tool (script "${script.id}" step ${stepNumber})`;
      }

      const toolId = step.tool.trim().toLowerCase();
      if (!K_VALID_TOOL_IDS.has(toolId)) {
        return `error: unknown_tool "${step.tool}" (script "${script.id}" step ${stepNumber})`;
      }

      // A script belongs to a step, so it must not move the run out from under
      // itself: the step it lands on plays its own on-start script immediately,
      // and two steps navigating to each other would ping-pong with no learner
      // input at all. Moving the run is the guide's CONVERSATIONAL tool and the
      // panel's buttons; authored branching is the step's `triggers` list.
      if (toolId === AgentToolNames.GoToWorkflowStep.toLowerCase()) {
        return (
          `error: script_cannot_navigate_the_run (script "${script.id}" step ${stepNumber} calls ` +
          `${AgentToolNames.GoToWorkflowStep}; branch with the step's triggers list instead)`
        );
      }

      // A read-only lookup would execute and return its text to nobody: a
      // learner mid-lesson is not reading a tool result. Refusing here is what
      // stops a script silently doing nothing for a whole step. `validate()`
      // says the same thing as a "Not a script command" issue the panel shows
      // — this is the belt for a document that reached playback anyway.
      if (isScriptContextTool(toolId)) {
        return (
          `error: script_step_is_a_lookup (script "${script.id}" step ${stepNumber} calls ` +
          `${step.tool}; resolve the target while authoring and write the resolved value into args)`
        );
      }

      // Structural commands have no transient overlay and no complete capture
      // shape. Letting one run locally while persistence is suppressed would
      // leave the in-memory project structurally different and allow that
      // state to leak into a later authoring write.
      if (isStructuralScriptCommand(toolId)) {
        return (
          `error: structural_script_command_not_supported (script "${script.id}" step ${stepNumber} calls ` +
          `${step.tool}; Workflow playback cannot create, delete, rename, reorder, or rewrite project structure)`
        );
      }

      // Arguments are a YAML mapping, so they are already an object or absent.
    }

    return null;
  }

  private static isDoNotWaitStep(step: WorkflowScript["steps"][number]): boolean {
    return !step.isSay
      && step.tool.trim().toLowerCase() === AgentToolNames.DoNotWait.toLowerCase();
  }

  private static isWaitForStep(step: WorkflowScript["steps"][number]): boolean {
    return !step.isSay
      && !step.isInvalid
      && step.tool.trim().toLowerCase() === AgentToolNames.WaitFor.toLowerCase();
  }

  private static isPlayVideoStep(step: WorkflowScript["steps"][number]): boolean {
    return !step.isSay
      && !step.isInvalid
      && step.tool.trim().toLowerCase() === AgentToolNames.PlayVideo.toLowerCase();
  }

  /** The play panel's label for a wait, so the learner is not looking at a stalled step. */
  private static describeWait(args: Record<string, unknown>): string {
    if (WorkflowScriptRunner.readWaitTrigger(args) === "delay") {
      const seconds = WorkflowScriptRunner.readDelaySeconds(args);
      return `Waiting ${seconds}s`;
    }
    return "Waiting for you";
  }

  private static readWaitTrigger(args: Record<string, unknown>): "delay" | "user_response" {
    const raw = args["trigger"];
    return typeof raw === "string" && raw.trim().toLowerCase() === "delay"
      ? "delay"
      : "user_response";
  }

  /**
   * Clamped into `(0, K_MAX_WAIT_SECONDS]`. An authored delay is a number
   * somebody typed, and a script that parks a learner for an hour is a typo
   * rather than an intention — but the step still has to end, so this clamps
   * rather than refusing.
   */
  private static readDelaySeconds(args: Record<string, unknown>): number {
    const raw = args["delaySeconds"];
    const parsed = typeof raw === "number" ? raw : Number(String(raw ?? "").trim());
    if (!Number.isFinite(parsed) || parsed <= 0) return K_DEFAULT_WAIT_SECONDS;
    return Math.min(parsed, K_MAX_WAIT_SECONDS);
  }

  /**
   * Parks the script until the learner acts, or for a fixed delay.
   *
   * Both paths race the run's cancellation, so navigating away (Next, Back, a
   * trigger, Stop) unwinds the wait immediately instead of leaving the revert
   * to settle a minute later — the same reason `stopSpeaking` exists.
   *
   * A `user_response` wait ALSO carries a ceiling. A learner who walks away
   * would otherwise hold the script — and its revert — open for the tab's
   * life, and a workflow that never resumes is worse than one that moves on.
   */
  /** The host's turn counter, or 0 when it does not keep one. */
  private readUserTurnCount(): number {
    return this.m_host.getUserTurnCount?.() ?? 0;
  }

  private async playWaitStepAsync(
    run: ActiveScriptRun,
    args: Record<string, unknown>,
    sinceTurnCount: number,
  ): Promise<void> {
    const trigger = WorkflowScriptRunner.readWaitTrigger(args);

    if (trigger === "delay") {
      const seconds = WorkflowScriptRunner.readDelaySeconds(args);
      await this.raceCancellation(run, (signal) => delayAsync(seconds * 1000, signal));
      return;
    }

    const waitForUserTurn = this.m_host.waitForUserTurnAsync;
    if (!waitForUserTurn) {
      // No seam (headless host, or a transport with no user-turn signal).
      // Degrade to a short pause rather than either hanging or racing straight
      // past a question the learner was just asked.
      console.warn(
        "[WorkflowScriptRunner] wait_for user_response: host has no user-turn signal; pausing briefly instead.",
      );
      await this.raceCancellation(run, (signal) =>
        delayAsync(K_DEFAULT_WAIT_SECONDS * 1000, signal),
      );
      return;
    }

    let turnArrived = false;
    await this.raceCancellation(run, async (signal) => {
      turnArrived = await waitForUserTurn.call(
        this.m_host,
        sinceTurnCount,
        K_USER_RESPONSE_TIMEOUT_MS,
        signal,
      );
    });

    // The learner's turn is SUBMITTED before it is answered, so resuming on it
    // alone puts the script's next say into the same window as the agent's
    // reply — two `response.create`s on one socket, and the second is dropped,
    // which loses a scripted line silently. Let the answer finish first. Only
    // after a real turn: a timeout or a cancel has no reply coming, and this
    // would spend its grace window waiting for one.
    if (turnArrived && !run.cancelled) {
      await this.m_host.waitForResponseIdleAsync(K_SAY_TIMEOUT_MS);
    }
  }

  /** Runs `work` with an abort signal that fires as soon as the run is cancelled. */
  private async raceCancellation(
    run: ActiveScriptRun,
    work: (signal: AbortSignal) => Promise<void>,
  ): Promise<void> {
    const controller = new AbortController();
    const poll = window.setInterval(() => {
      if (run.cancelled) controller.abort();
    }, K_CANCEL_POLL_MS);
    if (run.cancelled) controller.abort();

    try {
      await work(controller.signal);
    } catch (e) {
      // An aborted wait is the normal cancellation path, not a failure — it
      // must not fall through to the loop's catch and report `failed`.
      if (!controller.signal.aborted) throw e;
    } finally {
      window.clearInterval(poll);
    }
  }

  /** Unity `ShouldOverlapReplayStep`: `do_not_wait` affects the step before it. */
  private static shouldOverlapStep(script: WorkflowScript, currentIndex: number): boolean {
    const next = script.steps[currentIndex + 1];
    return !!next && WorkflowScriptRunner.isDoNotWaitStep(next);
  }

  private static isFailureResult(result: string): boolean {
    const normalized = result.trim().toLowerCase();
    return normalized.startsWith("error:")
      || normalized.startsWith("tool_error:")
      || normalized.startsWith("tool_not_supported:");
  }
}

/**
 * Keeps a run's outstanding-tool count honest, including across the
 * `do_not_wait` overlap path, so a stop can tell whether reverting right now
 * would race a write that has been dispatched but has not landed.
 *
 * A run skipped for that reason is not orphaned: its loop is either awaiting
 * this task or will reach its top-of-loop cancel check once this task settles,
 * and the loop's `finally` settles the revert either way.
 *
 * Deliberately a free function — it is bookkeeping on the run object, not
 * behaviour of the runner.
 */
function trackToolStep(run: ActiveScriptRun, start: () => Promise<void>): Promise<void> {
  // Mark the run busy BEFORE invoking the host. A host can synchronously stop
  // playback while constructing its promise; incrementing afterwards leaves a
  // window where stop settles the run and teardown releases persistence ahead
  // of that just-dispatched write.
  run.pendingToolCount++;
  let task: Promise<void>;
  try {
    task = start();
  } catch (error) {
    run.pendingToolCount--;
    return Promise.reject(error);
  }
  const tracked = task.finally(() => {
    run.pendingToolCount--;
    run.pendingToolTasks.delete(tracked);
  });
  run.pendingToolTasks.add(tracked);
  return tracked;
}
